import { DELIVERABLE_STATUS_LABELS, type DeliverableStatus } from "@dream/config/constants";
import type { ResolvedOccurrence } from "@dream/domain";
import { IconClock, StatusBadge } from "@dream/ui";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { ChipPerson } from "../lib/people.ts";
import { formatRelativeWhen } from "../lib/schedule-copy.ts";
import { AvatarStack } from "./avatar-stack.tsx";

export interface UpcomingRailItem {
  id: string;
  deliverableId: string;
  title: string;
  startsAt: Date;
  isAllDay: boolean;
  status: DeliverableStatus;
  typeLabel: string | null;
  typeSlug: string | null;
  typeIcon: string | null;
  typeColor: string;
  clientName: string | null;
  people: ChipPerson[];
}

export function occurrencesToUpcomingItems(
  occurrences: ResolvedOccurrence[],
  types: Array<{ id: string; label: string; color: string; slug: string; icon: string | null }>,
  clients: Array<{ id: string; name: string }>,
  now = new Date(),
  limit = 8,
  team: Array<{
    id: string;
    name: string;
    image?: string | null;
    avatarColor?: string | null;
  }> = [],
): UpcomingRailItem[] {
  const typeMap = new Map(types.map((type) => [type.id, type]));
  const clientMap = new Map(clients.map((client) => [client.id, client.name]));
  const teamMap = new Map(team.map((member) => [member.id, member]));
  return occurrences
    .filter(
      (item) =>
        item.status !== "cancelled" && item.startsAt.getTime() >= now.getTime() - 30 * 60 * 1000,
    )
    .sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime())
    .slice(0, limit)
    .map((item) => {
      const type = typeMap.get(item.typeId);
      const assignee = item.assigneeId === null ? undefined : teamMap.get(item.assigneeId);
      return {
        id: item.occurrenceId,
        deliverableId: item.deliverableId,
        title: item.title,
        startsAt: item.startsAt,
        isAllDay: item.isAllDay,
        status: item.status,
        typeLabel: type?.label ?? null,
        typeSlug: type?.slug ?? null,
        typeIcon: type?.icon ?? null,
        typeColor: type?.color ?? "#0074FF",
        clientName: clientMap.get(item.clientId) ?? null,
        people:
          assignee === undefined
            ? []
            : [
                {
                  id: assignee.id,
                  name: assignee.name,
                  imageUrl: assignee.image ?? null,
                  color: assignee.avatarColor ?? null,
                },
              ],
      };
    });
}

export function UpcomingRail({
  items,
  timezone,
  heading,
  empty,
  showClient = false,
  hideWhenEmpty = false,
  onOpen,
}: {
  items: UpcomingRailItem[];
  timezone: string;
  heading: string;
  empty: string;
  showClient?: boolean;
  /** No portal, faixa vazia só ocupava a viewport sem entregar informação. */
  hideWhenEmpty?: boolean;
  onOpen: (deliverableId: string) => void;
}) {
  if (hideWhenEmpty && items.length === 0) return null;

  return (
    <section
      data-testid="upcoming-rail"
      aria-label={heading}
      className="shrink-0 border-b border-[var(--color-line)] bg-[color-mix(in_srgb,var(--color-surface-subtle)_78%,var(--color-surface))]"
    >
      <div className="flex items-center gap-2.5 px-3 pt-2.5 sm:px-4 sm:pt-3 lg:px-5">
        <span
          aria-hidden="true"
          className="flex size-6 shrink-0 items-center justify-center rounded-md bg-[var(--dream-info-bg)] text-[var(--dream-info-fg)]"
        >
          <IconClock className="size-3.5" />
        </span>
        <h2 className="min-w-0 flex-1 truncate text-[0.75rem] font-semibold uppercase tracking-[0.06em] text-[var(--color-ink-secondary)]">
          {heading}
        </h2>
        {items.length > 0 ? (
          <p className="inline-flex h-5 shrink-0 items-center rounded-full bg-[var(--color-surface)] px-2 text-[0.6875rem] font-semibold tabular-nums text-[var(--color-ink-secondary)] shadow-[var(--shadow-xs)]">
            {items.length} {items.length === 1 ? "próxima" : "próximas"}
          </p>
        ) : null}
      </div>
      {items.length === 0 ? (
        <p className="px-3 pb-3 pt-2 text-[0.8125rem] text-[var(--color-ink-secondary)] sm:px-4 lg:px-5">
          {empty}
        </p>
      ) : (
        <div className="dream-rail-fade">
          <div className="dream-scroll-x flex snap-x snap-mandatory gap-2.5 overflow-x-auto px-3 py-2.5 sm:gap-3 sm:px-4 sm:py-3 lg:px-5">
            {items.map((item, index) => {
              const when = formatRelativeWhen(item.startsAt, timezone, item.isAllDay);
              const soon = when.day === "Hoje" || when.day === "Amanhã";
              const meta = [item.typeLabel, showClient ? item.clientName : null]
                .filter((part) => part !== null && part !== "")
                .join(" · ");
              return (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={`${item.title} · ${when.day} · ${when.time}`}
                      onClick={() => onOpen(item.deliverableId)}
                      style={{ animationDelay: `${index * 45}ms` }}
                      className={[
                        "dream-enter dream-float group relative flex min-w-[15.5rem] max-w-[20rem] shrink-0 snap-start flex-col",
                        "rounded-2xl bg-[var(--color-surface)]",
                        "text-left shadow-[var(--dream-shadow-pill,0_4px_20px_rgb(18_18_18_/_0.08))]",
                        "hover:shadow-[var(--dream-shadow-chip-hover)]",
                        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
                        "sm:min-w-[16.75rem]",
                      ].join(" ")}
                    >
                      <span className="flex flex-col gap-2 px-3.5 py-2.5">
                        <span className="flex items-center gap-2">
                          <span
                            className={[
                              "inline-flex h-6 min-w-0 max-w-[70%] items-center gap-1.5 truncate rounded-full px-2 text-[0.6875rem] font-semibold tabular-nums",
                              soon
                                ? "bg-[var(--dream-info-bg)] text-[var(--dream-info-fg)]"
                                : "bg-[var(--color-surface-muted)] text-[var(--color-ink-secondary)]",
                            ].join(" ")}
                          >
                            {soon ? (
                              <span
                                aria-hidden="true"
                                className="dream-soon-pulse size-1.5 shrink-0 rounded-full bg-[var(--dream-info-fg)]"
                              />
                            ) : null}
                            {when.day} · {when.time}
                          </span>
                          <span className="ml-auto shrink-0">
                            <StatusBadge status={item.status} size="sm" />
                          </span>
                        </span>
                        <span className="flex items-center gap-2.5">
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[0.8125rem] font-semibold leading-5 text-[var(--color-ink)]">
                              {item.title}
                            </span>
                            {meta !== "" ? (
                              <span className="mt-0.5 block truncate text-[0.6875rem] text-[var(--color-ink-secondary)]">
                                {meta}
                              </span>
                            ) : null}
                          </span>
                          <AvatarStack people={item.people} size={32} />
                        </span>
                      </span>
                      <span className="sr-only">{DELIVERABLE_STATUS_LABELS[item.status]}</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start" className="space-y-0.5">
                    <p className="font-semibold">{item.title}</p>
                    <p className="text-[11px] font-medium text-[var(--color-ink-inverse)]/80">
                      {when.day} · {when.time}
                      {meta !== "" ? ` · ${meta}` : ""}
                    </p>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
